import { getUserAndToken } from '@chegg-wt/core/api/user'
import { Config } from '@chegg-wt/core/config'
import { AppState, LogicDeps } from '@chegg-wt/core/state'
import {
  User,
  CITATIONS_JWT_NULL_CHEGG_TOKENS_ERROR,
  CitationsJwtError,
  AuthPayload,
} from '@chegg-wt/core/types/auth'
import { getCurrentTime } from '@chegg-wt/core/utils/dateTime'
import { validateJwt } from '@chegg-wt/core/utils/jwt'
import {
  deleteUser,
  getLocalStorageItem,
  LOCAL_STORAGE_KEYS,
  setEbUser,
  deleteLocal,
} from '@chegg-wt/core/utils/localStorage'
import { addQueryStringToRoute, routeToHref } from '@chegg-wt/core/utils/routes'
import {
  hasLoggedOutExternally,
  mapJwtEmailToUser,
  tokenIsCurrent,
} from '@chegg-wt/core/utils/token'
import { createLogic } from 'redux-logic'
import {
  setSessionStorageItem,
  SESSION_STORAGE_KEYS,
} from '@chegg-wt/core/utils/sessionStorage'
import { ErrorMsgs } from '@chegg-wt/core/constants/errorMsgs'
import { MIGRATION_STATUSES } from '@chegg-wt/core/constants/user'
import {
  hasPaperUploaded,
  setPaperUploaded as setPaperUploadedInCookie,
  removePaperUploadedCookie,
} from '@chegg-wt/core/utils/paperUploadedCookie'
import { FSACreatorPayload } from '@chegg-wt/core/state/types'
import { getType } from 'typesafe-actions'
import { refreshToken, logout as oneAuthLogout } from '@chegg/one-auth-sdk'
import { Cookies, removeCookie } from '@chegg-wt/core/utils/cookies'
import { proofReaderTransferOfOwnership } from '@chegg-wt/core-wa/state/actions'
import {
  AuthService,
  emitSignInSuccessEvent,
  emitSignUpSuccessEvent,
} from '../instrumentation/actions'
import { initializedSelector } from '../app/selectors'
import { fetchProjects } from '../projects/actions'
import { initSBM, navigated, jwtInitialized } from '../app/actions'
import { displayError } from '../errors/actions'
import { getRioClient } from '../instrumentation/rio/rioAnalytics'
import {
  fetchUser,
  setUser,
  dataMigrationStatus,
  setUserError,
  logoutFailed,
  getUserError,
  endDataMigrationTimer,
  dataMigrationTick,
  startDataMigrationTimer,
  handleCorruptToken,
  logout,
  authComplete,
  setPaperUploaded,
} from './actions'
import { ActionTypes } from './constants'
import { LoginPayload, FetchUserPayload, FetchUserMeta } from './types'
import {
  jwtSelector,
  migrationIdSelector,
  isGuestSelector,
  loggedInSelector,
  migrationStatusSelector,
} from './selectors'

export interface AuthSuccessGrant {
  access_token: string | null
  auth_flow?: 'code'
  auth_method?:
    | 'Chegg'
    | 'Facebook'
    | 'Google'
    | 'chegg'
    | 'facebook'
    | 'google'
    | 'apple'
  auth_provider_token?: string | null
  auth_type?: 'login' | 'signup'
  code?: string
  id_token: string | null
  redirect_uri?: string
}

export const loginLogic = createLogic<AppState, LoginPayload, any, LogicDeps>({
  type: ActionTypes.LOGIN,
  async process({ action }) {
    const { saOptions, redirectTo } = action.payload

    deleteUser()

    sessionStorage.setItem(SESSION_STORAGE_KEYS.ucfLoginSuccess, 'true')
    return fetchUser(redirectTo, saOptions.auth_type, saOptions.auth_method)
  },
})

export const fetchUserLogic = createLogic<
  AppState,
  FetchUserPayload,
  FetchUserMeta,
  LogicDeps
>({
  type: getType(fetchUser),
  async process({ action, api, routes }, dispatch, done) {
    const { redirectTo } = action.payload
    const { authMethod, authType } = action.meta

    try {
      setSessionStorageItem(SESSION_STORAGE_KEYS.fetchingToken, 'true')
      const user = await getUserAndToken()

      getRioClient().updateState({
        cheggUuid: user.chegg_uuid,
      })

      if (authType === 'login') {
        dispatch(
          emitSignInSuccessEvent({
            authService: authMethod.toLowerCase() as AuthService,
          })
        )
      } else if (authType === 'signup') {
        dispatch(
          emitSignUpSuccessEvent({
            authService: authMethod.toLowerCase() as AuthService,
          })
        )
      }

      const error = validateJwt(user.jwt)
      if (error) {
        return dispatch(handleCorruptToken(error))
      }
      setEbUser(user)

      if (sessionStorage.getItem(SESSION_STORAGE_KEYS.ucfLoginSuccess)) {
        await api.bff.transferOwnership()
        // TODO: change this method and user next/router
        const url = window.location.href
        const hasWritingAssistant = url.includes('/writing-assistant')
        if (hasWritingAssistant) {
          dispatch(proofReaderTransferOfOwnership({ routes }))
        }
        sessionStorage.removeItem(SESSION_STORAGE_KEYS.ucfLoginSuccess)
        sessionStorage.setItem(SESSION_STORAGE_KEYS.transferOwnership, 'true')
      }

      if (redirectTo) {
        // redirectTo occurs from a AuthPage, those pages have no ads
        // in order to get ads we need to refresh the page, especially
        // since ads bundle is added on server side
        window.location.href = routeToHref(redirectTo)
        return
      }
      dispatch(authComplete())
      dispatch(setUser(user))
    } catch (e) {
      dispatch(
        displayError(new Error('There was an error fetching your user information'))
      )
    } finally {
      done()
    }
  },
})

export const userMigrationLogic = createLogic<AppState, User, any, LogicDeps>({
  type: ActionTypes.SET_USER,
  validate({ action }, allow, reject) {
    const { status } = action.payload
    if (status === MIGRATION_STATUSES.complete) {
      allow(action)
    } else {
      // allows action to continue to other logics, but rejects this process
      reject(action)
    }
  },
  async process({ api, getState }, dispatch, done) {
    try {
      const status = await api.getMigrateStatusWithoutID(getState().user?.jwt)

      if (status === false) {
        dispatch(dataMigrationStatus(true))
        dispatch(startDataMigrationTimer())
      } else if (status) {
        const isLoggedIn = loggedInSelector(getState())
        if (isLoggedIn) await refreshToken()
        dispatch(fetchUser())
        dispatch(fetchProjects())
      }
    } finally {
      done()
    }
  },
})

export const userMigrationIdLogic = createLogic<AppState, User, any, LogicDeps>({
  type: ActionTypes.SET_USER,
  validate({ action }, allow, reject) {
    // BM,CM & CTFM legacy data has already been bulk migrated
    if (Config.skipMigrationStatusCheck) {
      reject(action)
    } else {
      const { migration_id: migrationId } = action.payload
      if (migrationId) {
        allow(action)
      } else {
        // allows action to continue to other logics, but rejects this process
        reject(action)
      }
    }
  },
  async process({ api, action }, dispatch, done) {
    const { migration_id: migrationId } = action.payload
    const status = await api.getMigrateStatus(migrationId)

    if (status === MIGRATION_STATUSES.inProgress) {
      dispatch(dataMigrationStatus(true))
      dispatch(startDataMigrationTimer())
    }

    if (status === MIGRATION_STATUSES.complete) {
      await refreshToken()
      dispatch(fetchUser())
      dispatch(fetchProjects())
    }
    done()
  },
})

export const refreshAndFetchLogic = createLogic<AppState, {}, any, LogicDeps>({
  type: ActionTypes.REFRESH_AND_FETCH,
  async process(_, dispatch, done) {
    try {
      refreshToken().then(async () => {
        await getUserAndToken()
        dispatch(fetchUser())
      })
    } catch (e) {
      dispatch(logout())
    }
    done()
  },
})

export const startDataMigrationTimerLogic = createLogic<AppState, {}, any, LogicDeps>({
  type: ActionTypes.START_DATA_MIGRATION_TIMER,
  warnTimeout: 0,
  cancelType: ActionTypes.END_DATA_MIGRATION_TIMER,
  process({ cancelled$ }, dispatch, done) {
    const TIME = 1000
    let interval = setTimeout(function repeat() {
      dispatch(dataMigrationTick())
      interval = setTimeout(repeat, TIME)
    }, TIME)

    // The declarative cancellation already stops future dispatches
    // but we should go ahead and stop the timer we created.
    // If cancelled, stop the time interval
    cancelled$.subscribe(() => {
      clearInterval(interval)
      done()
    })
  },
})

export const migrationTickLogicWithoutID = createLogic<AppState, {}, any, LogicDeps>({
  type: ActionTypes.DATA_MIGRATION_TICK,
  validate({ action, getState }, allow, reject) {
    const migrationId = migrationIdSelector(getState())
    const status = migrationStatusSelector(getState())
    if (status === MIGRATION_STATUSES.complete) allow(action)
    else if (!migrationId) reject(endDataMigrationTimer())
    else reject(action)
  },
  async process({ api, getState }, dispatch, done) {
    try {
      const status = await api.getMigrateStatusWithoutID(getState().user?.jwt)

      if (status === undefined || status === null) {
        dispatch(endDataMigrationTimer())
        dispatch(dataMigrationStatus(false))
      } else if (status) {
        dispatch(endDataMigrationTimer())
        dispatch(dataMigrationStatus(false))

        const isLoggedIn = loggedInSelector(getState())
        if (isLoggedIn) await refreshToken()

        dispatch(fetchUser())
        dispatch(fetchProjects())
      }
    } catch (err) {
      dispatch(endDataMigrationTimer())
      dispatch(dataMigrationStatus(false))
    }

    done()
  },
})

export const setIntervalDataMigrationLogic = createLogic<AppState, {}, any, LogicDeps>({
  type: ActionTypes.DATA_MIGRATION_TICK,
  validate({ action, getState }, allow, reject) {
    // BM,CM & CTFM legacy data has already been bulk migrated
    if (Config.skipMigrationStatusCheck) {
      reject(action)
    } else {
      const migrationId = migrationIdSelector(getState())
      const status = migrationStatusSelector(getState())
      if (migrationId) allow(action)
      else if (status !== MIGRATION_STATUSES.complete) reject(endDataMigrationTimer())
      else reject(action)
    }
  },
  async process({ api, getState }, dispatch, done) {
    const migrationId = migrationIdSelector(getState())
    const status = await api.getMigrateStatus(migrationId)

    if (status !== MIGRATION_STATUSES.inProgress) {
      dispatch(endDataMigrationTimer())
      dispatch(dataMigrationStatus(false))
    }

    if (status === MIGRATION_STATUSES.complete) {
      const isLoggedIn = loggedInSelector(getState())
      if (isLoggedIn) await refreshToken()

      dispatch(fetchUser())
      dispatch(fetchProjects())
    }

    done()
  },
})

export const setUserLogic = createLogic<AppState, User, any, LogicDeps>({
  type: ActionTypes.SET_USER,
  validate({ action }, allow, reject) {
    const error = validateJwt(action.payload.jwt)

    if (error) {
      return reject(handleCorruptToken(error))
    }

    return allow(setUser(mapJwtEmailToUser(action.payload)))
  },
  async process({ action }, dispatch, done) {
    const { payload: user } = action

    try {
      setEbUser(user)

      dispatch(initSBM())
      dispatch(jwtInitialized())
    } catch (error) {
      dispatch(setUserError(error))
    } finally {
      done()
    }
  },
})

const userIsCurrent = (user: User) =>
  tokenIsCurrent(user.jwt) && user.exp && user.exp > getCurrentTime()

export const getUserLogic = createLogic<AppState>({
  type: ActionTypes.GET_USER,
  process(_, dispatch, done) {
    let user = {} as User
    try {
      const localStorageString = getLocalStorageItem(LOCAL_STORAGE_KEYS.user)

      if (localStorageString) {
        user = JSON.parse(localStorageString)
      }
      if (userIsCurrent(user)) {
        dispatch(setUser(user))
      } else if (hasLoggedOutExternally(user.jwt)) {
        // TODO: remove once SA removes writing cookies on log out
        dispatch(logout())
      } else {
        dispatch(fetchUser())
      }
    } catch (error) {
      dispatch(getUserError(error))
    } finally {
      done()
    }
  },
})

export const logoutLogic = createLogic<AppState, {}, any, LogicDeps>({
  type: ActionTypes.LOGOUT,
  async process(_, dispatch, done) {
    try {
      await oneAuthLogout(true)
      removeCookie(Cookies.CheggUserAccessToken)
      removeCookie(Cookies.ViewedAdUnlock)
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.transferOwnership)
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.authSuccess)
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.role)
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.citationObject)
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.upgradeCitationObject)

      deleteUser()
      removePaperUploadedCookie()

      // After logging out we need to get a not_logged_in user which will also get ads bundle

      dispatch(fetchUser())
    } catch (error) {
      dispatch(logoutFailed(error))
    } finally {
      deleteLocal('citationCount')
      done()
    }
  },
})

export const upgradeLogic = createLogic<AppState, {}, any, LogicDeps>({
  type: ActionTypes.UPGRADE,
  async process() {
    await refreshToken().then(() => {
      const PAYMENT_UPGRADED = 'upgraded'
      const targetWin = document.querySelector(
        'iframe[src*="/users/paperchecker_payment_modal"]'
      )
      if (targetWin) {
        window.postMessage({ type: PAYMENT_UPGRADED }, targetWin as any)
      }
    })

    return fetchUser()
  },
})

export const handleCorruptTokenLogic = createLogic<
  AppState,
  CitationsJwtError,
  any,
  LogicDeps
>({
  type: ActionTypes.HANDLE_CORRUPT_TOKEN,
  async process({ routes, action: { payload: error } }, dispatch, done) {
    // When cheggTokens is null, this means a server-side refresh attempt failed, which means that
    // the refresh_token cookie is invalid (ie already used). This is effectively a 401 on refresh,
    // so the only way to get out of this state is to log out.
    if (error.type === CITATIONS_JWT_NULL_CHEGG_TOKENS_ERROR) {
      try {
        await oneAuthLogout(true)
        deleteUser()
        const redirect = window.location.href
        const route = addQueryStringToRoute(routes.toLogin(), { redirect })
        dispatch(navigated(route))
      } catch (error) {
        dispatch(logoutFailed(error))
      }
    } else {
      dispatch(displayError(new Error(ErrorMsgs.JWT_SET_USER_ERROR)))
    }

    done()
  },
})

export const initPaperCheckLogic = createLogic<AppState, {}, any, LogicDeps>({
  type: [jwtInitialized],
  async process({ api, getState }, dispatch, done) {
    const state = getState()
    const { sessionId, cheggUserUuid } = jwtSelector(state)
    const isGuest = isGuestSelector(state)
    const paperUploadedFromCookie = hasPaperUploaded(sessionId, cheggUserUuid)

    if (paperUploadedFromCookie) {
      dispatch(setPaperUploaded())
      return done()
    }

    if (isGuest) {
      return done()
    }

    try {
      const papers = await api.bff.getPapers({ limit: 1 })
      if (papers.length) {
        dispatch(setPaperUploaded(true))
      }
    } finally {
      done()
    }
  },
})

export const setPaperUploadedLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof setPaperUploaded>,
  any,
  LogicDeps
>({
  type: [setPaperUploaded],
  validate({ action, getState }, allow, reject) {
    const { setCookie } = action.payload
    const initialized = initializedSelector(getState())
    if (initialized && setCookie) {
      allow(action)
    } else {
      reject(action)
    }
  },
  process({ getState }, _, done) {
    const { sessionId, cheggUserUuid } = jwtSelector(getState())
    setPaperUploadedInCookie(sessionId, cheggUserUuid)
    done()
  },
})

export const startAuthFlowLogic = createLogic<AppState, AuthPayload>({
  type: ActionTypes.START_AUTH_FLOW,
  process({ action }, _, done) {
    try {
      const { defaultFlow, origin, redirectTo, alwaysParticipate } = action.payload
      const brand = Config.tokenSiteCode
      const AUTH_GATE_URL = Config.authgateUrl
      const ACTION = defaultFlow === 'signin' ? '' : '&action=signup'
      const redirect = `${
        redirectTo?.href || window.location.href
      }?authSuccess=true&origin=${origin}&alwaysParticipate=${alwaysParticipate}`
      const url = `${AUTH_GATE_URL}${brand}${ACTION}&redirect=${encodeURIComponent(
        `${redirect}`
      )}`

      window.location.href = url
    } catch (error) {
      alert('Failed to redirect to login. Please try again.')
    } finally {
      done()
    }
  },
})
